import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useController, useFormContext } from "react-hook-form";

import FormHelperText from "@mui/material/FormHelperText";
import Stack from "@mui/material/Stack";

import update from "immutability-helper";

import { error } from "app/themes/colors";
import CircularProgress from "components/circular-progress";
import SortableFileRow from "components/file-upload/sortable-file-row.component";
import { useGetBoxAccessTokenQuery } from "services/e-documents";
import type { Patient } from "services/types/patients.type";
import { formatBytes } from "utils/common";
import { MAX_FILE_SIZE } from "utils/validations.utils";

import UploadFileFromComputer from "./upload-file-from-computer/upload-file-from-computer.component";
import UploadFilesFromEDocs from "./upload-files-from-edocs/upload-files-from-edocs.component";
import { UPLOAD_FILE_ACCEPTED_TYPE_MAP } from "./utils";

type FileUploadWithEDocsProps = {
  direction?: "row" | "column" | "row-reverse" | "column-reverse";
  name: string;
  allowEDocs?: boolean;
  computerLabel?: ReactNode;
  eDocsLabel?: ReactNode;
  acceptedType?: string;
  sizeLimitLabel?: ReactNode;
  fileSizeLimit?: number;
  fileCountLimit?: number;
  totalFilesSizeLimit?: number;
  onPatientChange?: (patient: Patient) => void;
  setUploadWarningMessage: React.Dispatch<React.SetStateAction<string>>;
  patient?: Patient | null;
  onPatientSelected?: (patient: Patient | null) => void;
  onChangeBusyState?: (isLoading: boolean) => void;
};

export default function FileUploadComputerAndEDocs({
  name,
  direction = "row",
  allowEDocs = true,
  computerLabel = "Upload files from Computer",
  eDocsLabel = "Upload files from E-Documents",
  acceptedType,
  sizeLimitLabel = "total size up to 10 MB",
  fileSizeLimit = MAX_FILE_SIZE,
  totalFilesSizeLimit = MAX_FILE_SIZE,
  fileCountLimit = 10,
  setUploadWarningMessage,
  patient,
  onPatientSelected,
  onChangeBusyState
}: FileUploadWithEDocsProps) {
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    setIsUploading(false);
  }, []);

  const handleChangeLoadingState = useCallback((isLoading: boolean) => {
    setIsUploading(isLoading);
  }, []);

  const {
    data: boxAccessToken,
    isLoading,
    isFetching
  } = useGetBoxAccessTokenQuery(undefined, {
    skip: !allowEDocs
  });
  const {
    setValue,
    control,
    formState: { isSubmitting, errors },
    trigger
  } = useFormContext();

  const isBusy = isLoading || isFetching || isUploading;

  useEffect(() => {
    onChangeBusyState?.(isBusy);
  }, [isBusy, onChangeBusyState]);

  const acceptedTypeTitle = acceptedType
    ? UPLOAD_FILE_ACCEPTED_TYPE_MAP[acceptedType]
    : "";

  const { field } = useController({ name, control });
  const [filesList, setFilesList] = useState<File[]>([]);

  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleSelectFiles = useCallback(
    (newFilesList: File[]) => {
      const validFiles = newFilesList.filter((file) => {
        if (acceptedType && file.type !== acceptedType) {
          const fileTypeMessage = acceptedTypeTitle
            ? `${acceptedTypeTitle} files`
            : "files";

          setUploadWarningMessage(
            `Warning! Please only upload ${fileTypeMessage} that are under ${formatBytes(
              fileSizeLimit
            )}.`
          );
          return false;
        }

        if (file.size > fileSizeLimit) {
          setUploadWarningMessage(
            `Warning! Please upload files that are under ${formatBytes(
              fileSizeLimit
            )}.`
          );
          return false;
        }

        if (totalFilesSizeLimit && file.size > totalFilesSizeLimit) {
          setUploadWarningMessage(
            `Warning! Please upload files that are under ${formatBytes(
              totalFilesSizeLimit
            )}.`
          );
          return false;
        }

        return true;
      });

      if (totalFilesSizeLimit) {
        const filesTotalSize = [
          ...validFiles,
          ...Object.values(filesList)
        ].reduce((acc, currentFile) => acc + currentFile.size, 0);

        if (filesTotalSize > totalFilesSizeLimit) {
          setUploadWarningMessage(
            `Warning! Please upload no more than ${formatBytes(
              totalFilesSizeLimit
            )} of files at a time.`
          );
        }
      }

      if (fileCountLimit) {
        if (validFiles.length + filesList.length > fileCountLimit) {
          setUploadWarningMessage(
            `Warning! Please upload a maximum of ${fileCountLimit} files at a time.`
          );
        }
      }

      setFilesList((prevFiles) => [...prevFiles, ...validFiles]);
      const newValue = [...filesList, ...validFiles];
      field.onChange(newValue);
      trigger(name);
    },
    [
      acceptedType,
      acceptedTypeTitle,
      field,
      fileCountLimit,
      fileSizeLimit,
      filesList,
      name,
      setUploadWarningMessage,
      totalFilesSizeLimit,
      trigger
    ]
  );

  const onFileDrop = useCallback(
    (e: React.SyntheticEvent<EventTarget>) => {
      const target = e.target as HTMLInputElement;
      if (!target.files) return;

      const newFilesList = Object.values(target.files);
      handleSelectFiles(newFilesList);
    },
    [handleSelectFiles]
  );

  const removeFile = useCallback(
    (index: number) => {
      setFilesList((prevFiles) => {
        const newFilesList = prevFiles.toSpliced(index, 1);

        setValue(name, newFilesList);

        if (newFilesList.length <= 10) {
          setUploadWarningMessage("");
        }

        return newFilesList;
      });

      trigger(name);
    },
    [name, setValue, trigger, setUploadWarningMessage]
  );

  const moveRow = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      setFilesList((prevRows: File[]) => {
        setValue(
          name,
          update(prevRows, {
            $splice: [
              [dragIndex, 1],
              [hoverIndex, 0, prevRows[dragIndex] as File]
            ]
          })
        );
        return update(prevRows, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, prevRows[dragIndex] as File]
          ]
        });
      });
    },
    [name, setValue]
  );
  useEffect(() => {
    if (field.value && filesList.length === 0) {
      setFilesList(field.value);
    }
  }, [field.value, filesList]);

  useEffect(() => {
    if (isSubmitting) {
      setFilesList([]);
    }
  }, [isSubmitting]);

  return (
    <>
      <Stack
        width="100%"
        direction={direction}
        columnGap={2}
        rowGap={2}
        sx={{ boxSizing: "border-box" }}
      >
        <UploadFileFromComputer
          name={name}
          label={computerLabel}
          sizeLimitLabel={sizeLimitLabel}
          control={control}
          onFileDrop={onFileDrop}
          wrapperRef={wrapperRef}
        />
        {allowEDocs && boxAccessToken?.box_access_token && (
          <UploadFilesFromEDocs
            label={eDocsLabel}
            sizeLimitLabel={sizeLimitLabel}
            boxAccessToken={boxAccessToken?.box_access_token}
            fileSizeLimit={fileSizeLimit}
            patient={patient}
            onPatientSelected={onPatientSelected}
            onFilesSelected={handleSelectFiles}
            acceptedType={UPLOAD_FILE_ACCEPTED_TYPE_MAP[acceptedType || ""]}
            onChangeLoadingState={handleChangeLoadingState}
          />
        )}
      </Stack>

      <FormHelperText sx={{ textAlign: "center", my: 1, color: error[700] }}>
        <>{errors && errors[name] ? errors[name]?.message : ""}</>
      </FormHelperText>
      <DndProvider backend={HTML5Backend}>
        {filesList.length > 0 ? (
          <ol>
            <Stack
              spacing={2}
              sx={{
                my: 2,
                minWidth: "300px",
                minHeight: "50px",
                maxHeight: "150px"
              }}
            >
              {filesList.map((item, index) => {
                return (
                  <SortableFileRow
                    file={item}
                    index={index}
                    moveRow={moveRow}
                    id={item?.name + index}
                    key={item?.name + index}
                    removeFile={removeFile}
                  />
                );
              })}
            </Stack>
          </ol>
        ) : null}
      </DndProvider>
      {isBusy && <CircularProgress sx={{ py: 1 }} />}
    </>
  );
}
